/**
 * StepMcpSetup — Step 2 of onboarding.
 * Shows per-client MCP setup instructions across 4 tabs:
 * Claude Code, Claude Desktop, Codex CLI, Cursor.
 */

import { useState } from 'react';
import { buildMcpUrl } from '../../utils/mcpUrl';

// ---------------------------------------------------------------------------
// CopyButton — inline copy with "Copied!" feedback
// ---------------------------------------------------------------------------

interface CopyButtonProps {
  text: string;
  label?: string;
}

function CopyButton({ text, label = 'Copy' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      className="shrink-0 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200 rounded transition-colors"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// CodeBlock — monospace code snippet with copy button
// ---------------------------------------------------------------------------

interface CodeBlockProps {
  code: string;
  label?: string;
}

function CodeBlock({ code, label }: CodeBlockProps) {
  return (
    <div className="relative">
      <pre className="bg-gray-900/80 border border-gray-700 rounded-lg px-4 py-3 text-xs text-gray-200 font-mono whitespace-pre-wrap break-all overflow-x-auto">
        {code}
      </pre>
      <div className="absolute top-2 right-2">
        <CopyButton text={code} label={label} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab content
// ---------------------------------------------------------------------------

type TabId = 'claude-code' | 'claude-desktop' | 'codex' | 'cursor';

interface TabContentProps {
  token: string | null;
}

function ClaudeCodeTab({ token }: TabContentProps) {
  const mcpUrl = buildMcpUrl(token);
  const command = `claude mcp add --transport http sharpelab ${mcpUrl}`;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-300">Step 1 — Add the MCP server</p>
        <p className="text-xs text-gray-400">Run this command in your terminal:</p>
        <CodeBlock code={command} label="Claude Code command" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-300">Step 2 — Reload Claude Code</p>
        <p className="text-xs text-gray-400">
          Restart Claude Code or run <code className="bg-gray-800 px-1 rounded text-gray-300 font-mono">/reload</code> to pick up the new MCP server.
        </p>
      </div>
    </div>
  );
}

function ClaudeDesktopTab({ token }: TabContentProps) {
  const mcpUrl = buildMcpUrl(token);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-300">Step 1 — Open Claude Desktop settings</p>
        <p className="text-xs text-gray-400">
          Navigate to <span className="text-gray-300">Settings &rarr; Connectors &rarr; Add custom connector</span>.
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-300">Step 2 — Paste the MCP URL</p>
        <CodeBlock code={mcpUrl} label="MCP URL" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-300">Step 3 — Save</p>
        <p className="text-xs text-gray-400">Leave OAuth fields empty and save the connector.</p>
      </div>
    </div>
  );
}

function CodexTab({ token }: TabContentProps) {
  const mcpUrl = buildMcpUrl(token);
  const toml = `[mcp_servers.sharpelab]\nurl = "${mcpUrl}"`;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-300">Step 1 — Edit your Codex config</p>
        <p className="text-xs text-gray-400">
          Open <code className="bg-gray-800 px-1 rounded text-gray-300 font-mono">~/.codex/config.toml</code> and add:
        </p>
        <CodeBlock code={toml} label="Codex TOML config" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-300">Step 2 — Restart Codex</p>
        <p className="text-xs text-gray-400">Restart Codex to apply the new MCP server configuration.</p>
      </div>
    </div>
  );
}

function CursorTab({ token }: TabContentProps) {
  const mcpUrl = buildMcpUrl(token);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-300">Step 1 — Open Cursor MCP settings</p>
        <p className="text-xs text-gray-400">
          Navigate to <span className="text-gray-300">Settings &rarr; MCP &rarr; Add new MCP server</span>.
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-300">Step 2 — Paste the URL</p>
        <CodeBlock code={mcpUrl} label="Cursor MCP URL" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab button
// ---------------------------------------------------------------------------

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm rounded-lg transition-colors font-medium whitespace-nowrap ${
        active
          ? 'bg-primary-600/20 text-primary-300 border border-primary-600/40'
          : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/60 border border-transparent'
      }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface StepMcpSetupProps {
  token: string | null;
  onNext: () => void;
  onBack: () => void;
}

export function StepMcpSetup({ token, onNext, onBack }: StepMcpSetupProps) {
  const [activeTab, setActiveTab] = useState<TabId>('claude-code');

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'claude-code', label: 'Claude Code' },
    { id: 'claude-desktop', label: 'Claude Desktop' },
    { id: 'codex', label: 'Codex CLI' },
    { id: 'cursor', label: 'Cursor' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-white">Connect your agent</h2>
        <p className="text-gray-400 mt-1 text-sm">Pick your client below.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </div>

      {/* Tab content — framed so the per-client steps stand apart from the rest of the slide */}
      <div className="rounded-xl border border-gray-700 bg-gray-900/40 p-5 min-h-[200px]">
        {activeTab === 'claude-code' && <ClaudeCodeTab token={token} />}
        {activeTab === 'claude-desktop' && <ClaudeDesktopTab token={token} />}
        {activeTab === 'codex' && <CodexTab token={token} />}
        {activeTab === 'cursor' && <CursorTab token={token} />}
      </div>

      {/* Single nudge prompt */}
      <div className="rounded-xl border border-gray-700 bg-gray-800/40 p-4">
        <p className="text-xs font-semibold text-gray-300 mb-1">Once connected, try asking your agent:</p>
        <p className="text-sm text-gray-300 italic leading-snug">
          "Give me a quick tour of sharpelab — what tools you have and what's the simplest end-to-end flow I can run today."
        </p>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2.5 rounded-lg border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 transition-colors text-sm font-medium"
        >
          Back
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onNext}
          className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-medium text-sm transition-colors"
        >
          I've added the server
        </button>
      </div>
    </div>
  );
}
